import { useState, useCallback, useRef } from 'react';
import * as XLSX from 'xlsx';
import SEO from '../../components/SEO';
import AdUnit from '../../components/AdUnit';

function parseCSV(text, delimiter) {
  delimiter = delimiter || ',';
  var rows = [];
  var row = [];
  var field = '';
  var inQuotes = false;
  for (var i = 0; i < text.length; i++) {
    var c = text[i];
    if (inQuotes) {
      if (c === '"' && text[i + 1] === '"') { field += '"'; i++; }
      else if (c === '"') { inQuotes = false; }
      else { field += c; }
    } else {
      if (c === '"') { inQuotes = true; }
      else if (c === delimiter) { row.push(field); field = ''; }
      else if (c === '\n' || (c === '\r' && text[i + 1] === '\n')) {
        row.push(field); field = ''; rows.push(row); row = [];
        if (c === '\r') i++;
      } else { field += c; }
    }
  }
  if (field || row.length) { row.push(field); rows.push(row); }
  return rows;
}

function toCSVString(rows, delimiter) {
  delimiter = delimiter || ',';
  return rows.map(function (r) {
    return r.map(function (f) {
      if (f.indexOf(delimiter) >= 0 || f.indexOf('"') >= 0 || f.indexOf('\n') >= 0) {
        return '"' + f.replace(/"/g, '""') + '"';
      }
      return f;
    }).join(delimiter);
  }).join('\n');
}

function escapeXml(s) {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

function downloadBlob(blob, filename) {
  var url = URL.createObjectURL(blob);
  var a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

function downloadText(text, filename, mime) {
  downloadBlob(new Blob([text], { type: mime || 'text/plain' }), filename);
}

var MODES = [
  { id: 'json', label: 'CSV → JSON' },
  { id: 'xml', label: 'CSV → XML' },
  { id: 'yaml', label: 'CSV → YAML' },
  { id: 'tsv', label: 'CSV → TSV' },
  { id: 'excel', label: 'CSV → Excel' },
  { id: 'excel2csv', label: 'Excel → CSV' },
  { id: 'delimiter', label: 'Change Delimiter' },
  { id: 'validate', label: 'Validate CSV' },
  { id: 'sort', label: 'Sort CSV' },
];

var MAX_FILE_SIZE = 5 * 1024 * 1024; // 5 MB

function CsvTools() {
  var [mode, setMode] = useState('json');
  var [input, setInput] = useState('name,age,city\nAlice,30,Mumbai\nBob,25,Delhi\nCharlie,35,Bangalore');
  var [delimiter, setDelimiter] = useState(',');
  var [newDelimiter, setNewDelimiter] = useState('|');
  var [sortCol, setSortCol] = useState(0);
  var [sortDir, setSortDir] = useState('asc');
  var [hasHeader, setHasHeader] = useState(true);
  var [excelStatus, setExcelStatus] = useState('');
  var fileInputRef = useRef(null);

  var handleFileUpload = useCallback(function (e) {
    var file = e.target.files[0];
    if (!file) return;
    if (file.size > MAX_FILE_SIZE) {
      setExcelStatus('File too large (' + (file.size / 1024 / 1024).toFixed(1) + ' MB). Max allowed is 5 MB.');
      e.target.value = '';
      return;
    }
    var ext = file.name.split('.').pop().toLowerCase();

    if (ext === 'csv' || ext === 'tsv' || ext === 'txt') {
      var reader = new FileReader();
      reader.onload = function (ev) {
        setInput(ev.target.result);
        if (ext === 'tsv') setDelimiter('\t');
      };
      reader.readAsText(file);
    } else if (ext === 'xlsx' || ext === 'xls') {
      var reader2 = new FileReader();
      reader2.onload = function (ev) {
        try {
          var wb = XLSX.read(ev.target.result, { type: 'array' });
          var ws = wb.Sheets[wb.SheetNames[0]];
          var csv = XLSX.utils.sheet_to_csv(ws);
          setInput(csv);
          setDelimiter(',');
          setMode('json');
          setExcelStatus('Loaded "' + file.name + '" (' + wb.SheetNames[0] + ')');
        } catch (err) {
          setExcelStatus('Error reading Excel: ' + err.message);
        }
      };
      reader2.readAsArrayBuffer(file);
    }
    e.target.value = '';
  }, []);

  var handleDownloadExcel = useCallback(function () {
    try {
      var rows = parseCSV(input, delimiter);
      if (!rows.length) return;
      var ws = XLSX.utils.aoa_to_sheet(rows);
      var wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Sheet1');
      var buf = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
      downloadBlob(new Blob([buf], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }), 'data.xlsx');
    } catch (err) {
      setExcelStatus('Error: ' + err.message);
    }
  }, [input, delimiter]);

  var getDownloadInfo = function () {
    if (mode === 'json') return { ext: 'json', mime: 'application/json' };
    if (mode === 'xml') return { ext: 'xml', mime: 'application/xml' };
    if (mode === 'yaml') return { ext: 'yaml', mime: 'text/yaml' };
    if (mode === 'tsv') return { ext: 'tsv', mime: 'text/tab-separated-values' };
    if (mode === 'delimiter' || mode === 'sort') return { ext: 'csv', mime: 'text/csv' };
    return null;
  };

  var process = useCallback(function () {
    try {
      if (mode === 'excel') return { output: '(Click "Download .xlsx" to export your CSV as an Excel file)', error: null };
      if (mode === 'excel2csv') return { output: '(Upload an .xlsx or .xls file using the button above — it will be converted to CSV automatically)', error: null };

      var rows = parseCSV(input, delimiter);
      if (!rows.length) return { output: '', error: 'No data found.' };
      if (rows.length > 10000) return { output: '', error: 'Too many rows (' + rows.length.toLocaleString() + '). Max 10,000 rows for live preview. Use file upload + download instead.' };
      var headers = hasHeader ? rows[0] : rows[0].map(function (_, i) { return 'col' + (i + 1); });
      var data = hasHeader ? rows.slice(1) : rows;

      if (mode === 'json') {
        var arr = data.map(function (r) {
          var obj = {};
          headers.forEach(function (h, i) { obj[h.trim()] = r[i] !== undefined ? r[i].trim() : ''; });
          return obj;
        });
        return { output: JSON.stringify(arr, null, 2), error: null };
      }
      if (mode === 'xml') {
        var xml = '<?xml version="1.0" encoding="UTF-8"?>\n<records>\n';
        data.forEach(function (r) {
          xml += '  <record>\n';
          headers.forEach(function (h, i) {
            var tag = h.trim().replace(/[^a-zA-Z0-9_]/g, '_');
            xml += '    <' + tag + '>' + escapeXml(r[i] !== undefined ? r[i].trim() : '') + '</' + tag + '>\n';
          });
          xml += '  </record>\n';
        });
        xml += '</records>';
        return { output: xml, error: null };
      }
      if (mode === 'yaml') {
        var yaml = '';
        data.forEach(function (r) {
          yaml += '- ';
          headers.forEach(function (h, i) {
            var val = r[i] !== undefined ? r[i].trim() : '';
            yaml += (i === 0 ? '' : '  ') + h.trim() + ': ' + (val.match(/^[0-9.]+$/) ? val : '"' + val.replace(/"/g, '\\"') + '"') + '\n';
          });
        });
        return { output: yaml.trim(), error: null };
      }
      if (mode === 'tsv') {
        var allRows = hasHeader ? [headers].concat(data) : data;
        return { output: toCSVString(allRows, '\t'), error: null };
      }
      if (mode === 'delimiter') {
        var allR = hasHeader ? [headers].concat(data) : data;
        return { output: toCSVString(allR, newDelimiter), error: null };
      }
      if (mode === 'validate') {
        var issues = [];
        var colCount = rows[0].length;
        rows.forEach(function (r, i) {
          if (r.length !== colCount) issues.push('Row ' + (i + 1) + ': expected ' + colCount + ' columns, found ' + r.length);
          r.forEach(function (f, j) {
            if (f.indexOf('"') >= 0 && (f.match(/"/g) || []).length % 2 !== 0) issues.push('Row ' + (i + 1) + ', Col ' + (j + 1) + ': unmatched quote');
          });
        });
        var summary = '\u2705 Total rows: ' + rows.length + '\n\u2705 Columns: ' + colCount;
        if (hasHeader) summary += '\n\u2705 Headers: ' + headers.join(', ');
        if (issues.length === 0) summary += '\n\n\u2705 CSV is valid \u2014 no issues found.';
        else summary += '\n\n\u26a0\ufe0f Issues found (' + issues.length + '):\n' + issues.join('\n');
        return { output: summary, error: null };
      }
      if (mode === 'sort') {
        var colIdx = Math.min(sortCol, headers.length - 1);
        var sorted = data.slice().sort(function (a, b) {
          var va = (a[colIdx] || '').trim();
          var vb = (b[colIdx] || '').trim();
          var na = parseFloat(va);
          var nb = parseFloat(vb);
          if (!isNaN(na) && !isNaN(nb)) return sortDir === 'asc' ? na - nb : nb - na;
          return sortDir === 'asc' ? va.localeCompare(vb) : vb.localeCompare(va);
        });
        var sortedRows = hasHeader ? [headers].concat(sorted) : sorted;
        return { output: toCSVString(sortedRows, delimiter), error: null };
      }
      return { output: '', error: 'Unknown mode.' };
    } catch (e) {
      return { output: '', error: 'Error: ' + e.message };
    }
  }, [input, delimiter, newDelimiter, mode, sortCol, sortDir, hasHeader]);

  var result = process();
  var parsedHeaders = parseCSV(input, delimiter);
  var headerNames = parsedHeaders.length > 0 ? parsedHeaders[0] : [];

  return (
    <div className="page-prettifier">
      <SEO title="CSV Tools - Convert CSV to JSON, Excel, XML, YAML Online Free" description="Free online CSV tools. Convert CSV to JSON, Excel, XML, YAML, TSV. Upload Excel to CSV. Change delimiters, validate, and sort. All in your browser." path="/csv-tools" />
      <div className="container">
        <div className="tool-header">
          <h1>CSV Tools</h1>
          <p>Convert, validate, and transform CSV data — upload files or paste text. Everything stays in your browser.</p>
        </div>
        <AdUnit />

        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginBottom: '1.5rem' }}>
          {MODES.map(function (m) {
            return (
              <button key={m.id} onClick={function () { setMode(m.id); setExcelStatus(''); }}
                className={mode === m.id ? 'btn btn-primary' : 'btn btn-secondary'}
                style={{ fontSize: '0.85rem', padding: '0.5rem 1rem' }}>
                {m.label}
              </button>
            );
          })}
        </div>

        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.75rem', marginBottom: '1.5rem', alignItems: 'center' }}>
          <input ref={fileInputRef} type="file" accept=".csv,.tsv,.txt,.xlsx,.xls" onChange={handleFileUpload}
            style={{ display: 'none' }} />
          <button className="btn btn-secondary" onClick={function () { fileInputRef.current.click(); }}
            style={{ fontSize: '0.85rem', padding: '0.5rem 1rem' }}>
            {mode === 'excel2csv' ? '\uD83D\uDCC2 Upload Excel File' : '\uD83D\uDCC2 Upload CSV / Excel File'}
          </button>
          {excelStatus && <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>{excelStatus}</span>}
        </div>

        <div className="calc-layout">
          <div className="calc-inputs" style={{ flex: 1 }}>
            <div className="form-group">
              <label htmlFor="csv-input">CSV Input</label>
              <textarea id="csv-input" value={input} onChange={function (e) { setInput(e.target.value); }}
                className="color-input" rows={12} style={{ fontFamily: 'monospace', fontSize: '0.85rem', width: '100%', resize: 'vertical' }}
                placeholder="Paste your CSV data here or upload a file..." />
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1rem' }}>
              <div className="form-group" style={{ flex: 1, minWidth: '120px' }}>
                <label htmlFor="csv-delim">Delimiter</label>
                <select id="csv-delim" value={delimiter} onChange={function (e) { setDelimiter(e.target.value); }} className="indent-select" style={{ width: '100%' }}>
                  <option value=",">Comma (,)</option>
                  <option value=";">Semicolon (;)</option>
                  <option value="|">Pipe (|)</option>
                  <option value={'\t'}>Tab</option>
                </select>
              </div>
              <div className="form-group" style={{ flex: 1, minWidth: '120px' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                  <input type="checkbox" checked={hasHeader} onChange={function (e) { setHasHeader(e.target.checked); }} />
                  First row is header
                </label>
              </div>
            </div>
            {mode === 'delimiter' && (
              <div className="form-group">
                <label htmlFor="csv-new-delim">New Delimiter</label>
                <select id="csv-new-delim" value={newDelimiter} onChange={function (e) { setNewDelimiter(e.target.value); }} className="indent-select" style={{ width: '100%' }}>
                  <option value=",">Comma (,)</option>
                  <option value=";">Semicolon (;)</option>
                  <option value="|">Pipe (|)</option>
                  <option value={'\t'}>Tab</option>
                </select>
              </div>
            )}
            {mode === 'sort' && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1rem' }}>
                <div className="form-group" style={{ flex: 1, minWidth: '120px' }}>
                  <label htmlFor="csv-sort-col">Sort by Column</label>
                  <select id="csv-sort-col" value={sortCol} onChange={function (e) { setSortCol(parseInt(e.target.value)); }} className="indent-select" style={{ width: '100%' }}>
                    {headerNames.map(function (h, i) {
                      return <option key={i} value={i}>{hasHeader ? h : 'Column ' + (i + 1)}</option>;
                    })}
                  </select>
                </div>
                <div className="form-group" style={{ flex: 1, minWidth: '120px' }}>
                  <label htmlFor="csv-sort-dir">Direction</label>
                  <select id="csv-sort-dir" value={sortDir} onChange={function (e) { setSortDir(e.target.value); }} className="indent-select" style={{ width: '100%' }}>
                    <option value="asc">Ascending (A-Z, 0-9)</option>
                    <option value="desc">Descending (Z-A, 9-0)</option>
                  </select>
                </div>
              </div>
            )}
          </div>
          <div className="calc-results" style={{ flex: 1 }}>
            {mode === 'excel' ? (
              <div style={{ textAlign: 'center', padding: '2rem 1rem' }}>
                <p style={{ fontSize: '1rem', marginBottom: '1rem', color: 'var(--text-secondary)' }}>Your CSV data will be exported as an Excel (.xlsx) file.</p>
                <button className="btn btn-primary" style={{ fontSize: '1rem', padding: '0.75rem 2rem' }} onClick={handleDownloadExcel}>
                  Download .xlsx
                </button>
              </div>
            ) : (
              <>
                <div className="form-group" style={{ width: '100%' }}>
                  <label>Output {result.error && <span style={{ color: 'var(--error)' }}> — {result.error}</span>}</label>
                  <textarea readOnly value={result.output} rows={16}
                    className="color-input" style={{ fontFamily: 'monospace', fontSize: '0.85rem', width: '100%', resize: 'vertical' }} />
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginTop: '0.5rem' }}>
                  <button className="btn btn-primary"
                    onClick={function () { navigator.clipboard.writeText(result.output); }}>
                    Copy Output
                  </button>
                  {getDownloadInfo() && (
                    <button className="btn btn-secondary"
                      onClick={function () {
                        var info = getDownloadInfo();
                        downloadText(result.output, 'output.' + info.ext, info.mime);
                      }}>
                      Download .{getDownloadInfo().ext}
                    </button>
                  )}
                </div>
              </>
            )}
          </div>
        </div>

        <section className="tool-guide">
          <h2>About CSV Tools</h2>
          <p>This suite of CSV tools handles all common CSV operations right in your browser. No data is sent to any server — everything is processed locally for complete privacy. Upload files or paste data directly.</p>
          <h2>Available Operations</h2>
          <ul>
            <li>CSV to JSON — Convert tabular data to a JSON array of objects.</li>
            <li>CSV to XML — Generate well-formed XML from CSV rows.</li>
            <li>CSV to YAML — Output clean YAML from your CSV data.</li>
            <li>CSV to TSV — Convert comma-separated to tab-separated values.</li>
            <li>CSV to Excel — Export your CSV data as a downloadable .xlsx file.</li>
            <li>Excel to CSV — Upload an .xlsx or .xls file and convert it to CSV.</li>
            <li>Change Delimiter — Switch between comma, semicolon, pipe, or tab delimiters.</li>
            <li>Validate CSV — Check for structural issues like inconsistent column counts.</li>
            <li>Sort CSV — Sort rows by any column with smart number detection.</li>
          </ul>
        </section>
      </div>
    </div>
  );
}

export default CsvTools;
